import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const Cart = () => {
  const { 
    items, 
    totalItems,
    subtotalUSD, 
    subtotalBS, 
    deliveryFeeUSD,
    deliveryFeeBS,
    totalUSD, 
    totalBS, 
    exchangeRate,
    removeItem, 
    updateQuantity, 
    clearCart 
  } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center py-16">
          <div className="text-8xl mb-6">🛒</div>
          <h2 className="text-2xl font-serif font-bold text-primary mb-3">
            Tu carrito está vacío
          </h2>
          <p className="text-gray-500 mb-8">
            Explora nuestros productos y agrega tus favoritos
          </p>
          <Link to="/productos" className="btn-primary inline-flex">
            🍕 Ver productos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold mb-1">🛒 Tu carrito</h1>
        <p className="text-gray-500 text-sm">{totalItems} producto(s)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Items */}
        <div className="lg:col-span-2 space-y-3">
          {items.map(item => (
            <div 
              key={item.id} 
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-4"
            >
              <div className="w-20 h-20 rounded-xl bg-background-alt flex items-center justify-center text-4xl flex-shrink-0">
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-primary truncate">{item.name}</h3>
                    <div className="text-gold font-bold">${Number(item.price).toFixed(2)}</div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-gray-400 hover:text-red-500 p-1"
                    aria-label="Eliminar"
                  >
                    🗑️
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-background-alt rounded-full">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 rounded-full hover:bg-gold hover:text-white transition flex items-center justify-center font-bold"
                    >
                      −
                    </button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full hover:bg-gold hover:text-white transition flex items-center justify-center font-bold"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-primary">
                      ${(Number(item.price) * item.quantity).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Bs {(Number(item.price) * item.quantity * exchangeRate).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-between pt-4">
            <button
              onClick={() => navigate('/productos')}
              className="text-gold text-sm hover:underline"
            >
              ← Seguir comprando
            </button>
            <button
              onClick={clearCart}
              className="text-red-500 text-sm hover:underline"
            >
              🗑️ Vaciar carrito
            </button>
          </div>
        </div>

        {/* Resumen */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
            <h2 className="text-lg font-serif font-bold mb-4">Resumen</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <div className="text-right">
                  <div className="font-medium">${subtotalUSD.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Bs {subtotalBS.toFixed(2)}</div>
                </div>
              </div>
              {deliveryFeeUSD > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Envío</span>
                  <div className="text-right">
                    <div className="font-medium">${deliveryFeeUSD.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">Bs {deliveryFeeBS.toFixed(2)}</div>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="font-bold">Total</span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gold">${totalUSD.toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Bs {totalBS.toFixed(2)}</div>
                </div>
              </div>
              <div className="text-center text-xs text-gray-400 pt-1">
                Tasa: 1 USD = Bs {exchangeRate}
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="w-full mt-6 py-4 bg-gold text-white rounded-full font-bold hover:bg-gold-dark transition shadow-md"
            >
              {isAuthenticated ? 'Proceder al pago →' : '🔑 Iniciar sesión'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
